import { FuncionalidadeDTO } from "@/core/domain/dto/privado/aplicacao/modulo/funcionalidade.dto";
import { ModuloDTO } from "@/core/domain/dto/privado/aplicacao/modulo/modulo.dto";
import { PermissaoTokenDTO } from "@/core/domain/dto/seguranca/acesso/oauth/permissao-token.dto";
import { EntityStatusEnum } from "@/core/domain/enums/entity/entity-status.enum";
import { ContextoModuloEnum } from "@/core/domain/enums/privado/aplicacao/modulo/contexto-modulo.enum";

function mapPermissaoToFuncionalidade(permissao: PermissaoTokenDTO): FuncionalidadeDTO {
    const funcionalidade = new FuncionalidadeDTO();
    funcionalidade.id = permissao.funcionalidadeId;
    funcionalidade.nome = permissao.funcionalidadeNome;
    funcionalidade.icone = permissao.funcionalidadeIcone;
    funcionalidade.url = permissao.funcionalidadeUrl;
    funcionalidade.moduloId = permissao.funcionalidadeModuloId;
    funcionalidade.moduloNome = permissao.funcionalidadeModuloNome;
    funcionalidade.moduloIcone = permissao.funcionalidadeModuloIcone;
    funcionalidade.contexto = ContextoModuloEnum.TENANT;
    funcionalidade.index = permissao.funcionalidadeIndex ?? permissao.funcionalidadeId;
    funcionalidade.status = EntityStatusEnum.ATIVO;
    return funcionalidade;
}

function upsertModulo(
    modulosMap: Map<number, ModuloDTO>,
    id: number,
    nome?: string,
    icone?: string,
    index?: number,
    moduloPaiId?: number,
): ModuloDTO {
    const existing = modulosMap.get(id);
    if (existing) {
        if (nome) {
            existing.nome = nome;
        }
        if (icone) {
            existing.icone = icone;
        }
        if (index != null) {
            existing.index = index;
        }
        if (moduloPaiId != null) {
            existing.moduloPaiId = moduloPaiId;
        }
        return existing;
    }

    const modulo = new ModuloDTO();
    modulo.id = id;
    modulo.nome = nome;
    modulo.icone = icone;
    modulo.index = index ?? id;
    modulo.moduloPaiId = moduloPaiId;
    modulo.contexto = ContextoModuloEnum.TENANT;
    modulo.status = EntityStatusEnum.ATIVO;
    modulo.funcionalidades = [];
    modulosMap.set(id, modulo);
    return modulo;
}

export function buildModulosFromPermissoesOnly(
    permissoes: Array<PermissaoTokenDTO>,
): Array<ModuloDTO> {
    if (!permissoes?.length) {
        return [];
    }

    const modulosMap = new Map<number, ModuloDTO>();

    for (const permissao of permissoes) {
        if (!permissao?.funcionalidadeId || !permissao.funcionalidadeModuloId) {
            continue;
        }

        if (permissao.funcionalidadeModuloPaiId) {
            upsertModulo(
                modulosMap,
                permissao.funcionalidadeModuloPaiId,
                permissao.funcionalidadeModuloPaiNome,
                permissao.funcionalidadeModuloPaiIcone,
                permissao.funcionalidadeModuloPaiIndex,
            );
        }

        const modulo = upsertModulo(
            modulosMap,
            permissao.funcionalidadeModuloId,
            permissao.funcionalidadeModuloNome,
            permissao.funcionalidadeModuloIcone,
            permissao.funcionalidadeModuloIndex,
            permissao.funcionalidadeModuloPaiId,
        );

        const funcionalidade = mapPermissaoToFuncionalidade(permissao);
        if (!modulo.funcionalidades.some((item) => item.id === funcionalidade.id)) {
            modulo.funcionalidades.push(funcionalidade);
        }
    }

    return Array.from(modulosMap.values());
}

export function mergeModulosAncestorsFromFlatTree(
    modulos: Array<ModuloDTO>,
    flatTree: Array<ModuloDTO>,
): Array<ModuloDTO> {
    if (!modulos?.length || !flatTree?.length) {
        return modulos ?? [];
    }

    const flatById = new Map(flatTree.map((modulo) => [modulo.id, modulo]));
    const funcionalidadesById = new Map<number, FuncionalidadeDTO>();
    for (const modulo of flatTree) {
        for (const funcionalidade of modulo.funcionalidades ?? []) {
            funcionalidadesById.set(funcionalidade.id, funcionalidade);
        }
    }

    const modulosMap = new Map(modulos.map((modulo) => [modulo.id, { ...modulo }]));

    const ensureAncestor = (moduloId: number): void => {
        const flatModulo = flatById.get(moduloId);
        if (!flatModulo) {
            return;
        }

        if (!modulosMap.has(moduloId)) {
            modulosMap.set(moduloId, {
                ...flatModulo,
                funcionalidades: [],
                modulosFilhos: undefined,
            });
        } else {
            const existing = modulosMap.get(moduloId)!;
            existing.moduloPaiId = flatModulo.moduloPaiId;
            existing.moduloPaiNome = flatModulo.moduloPaiNome;
            existing.moduloPaiIcone = flatModulo.moduloPaiIcone;
            existing.index = flatModulo.index;
            existing.nome = flatModulo.nome;
            existing.icone = flatModulo.icone;
        }

        if (flatModulo.moduloPaiId != null) {
            ensureAncestor(flatModulo.moduloPaiId);
        }
    };

    for (const modulo of [...modulosMap.values()]) {
        if (modulo.moduloPaiId != null) {
            ensureAncestor(modulo.moduloPaiId);
        }
        modulo.funcionalidades = (modulo.funcionalidades ?? []).map((funcionalidade) => {
            const flatFuncionalidade = funcionalidadesById.get(funcionalidade.id);
            if (!flatFuncionalidade) {
                return funcionalidade;
            }
            return {
                ...funcionalidade,
                index: flatFuncionalidade.index,
                moduloIcone: flatFuncionalidade.moduloIcone ?? funcionalidade.moduloIcone,
            };
        });
    }

    return Array.from(modulosMap.values());
}
